/**
 * Top-level error boundary so a render-time exception in any page or
 * component shows a visible error panel instead of unmounting the
 * whole React tree to a blank document. The fallback prints the error
 * message + stack so we can debug from the live URL without DevTools.
 */

import { Component, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          padding: "32px",
          background: "#0a0d12",
          color: "#e8ecf2",
          fontFamily: "ui-monospace, monospace",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <h1
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#ffa198",
              marginBottom: 12,
              letterSpacing: "0.04em",
            }}
          >
            Render error
          </h1>
          <p style={{ color: "#9aa6b3", marginBottom: 16 }}>
            Something threw while rendering this view. The full message is
            below — paste it in chat and we'll fix it.
          </p>
          <pre
            style={{
              background: "#000206",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 6,
              padding: 14,
              overflow: "auto",
              maxHeight: 360,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: "#ffa198",
            }}
          >
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </pre>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={this.reset}
              style={{
                padding: "8px 14px",
                background: "rgba(196,163,255,0.14)",
                border: "1px solid rgba(196,163,255,0.45)",
                borderRadius: 6,
                color: "#c4a3ff",
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 14px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: 6,
                color: "#9aa6b3",
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
            <a
              href="/"
              style={{
                padding: "8px 14px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: 6,
                color: "#9aa6b3",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Back to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
